// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ButtonHTMLAttributes, type ReactNode } from 'react';

import { Button, Flex, Space, type MantineColor, Box, type ButtonVariant } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { MODAL_DIVIDER_BORDER } from 'components/Mantine/Modals/helpers';
import { isNotNil } from 'helpers/isNotNil';

export type ModalButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  /** Confirmation button label */
  confirmationText?: ReactNode;
  /** Confirmation button tooltip */
  confirmationTooltip?: ReactNode;
  /** How many records this confirmation affects (if applicable) */
  confirmationCount?: number;
  /** Confirmation button color */
  buttonColor?: MantineColor;
  /** Confirmation button variant */
  buttonVariant?: ButtonVariant;
  /** Cancellation button label */
  cancellationText?: string;
  /** Component to render to the left of the `ModalButtons` */
  leftComponent?: ReactNode;
  /** Sets `loading` on the Confirmation button, and disables the Cancellation button */
  loading?: boolean;
  /** Sets `disabled` on the Confirmation button */
  disabled?: boolean;
  /** If true, will hide the divider and remove the padding */
  hideDivider?: boolean;
} & (
  | ({
      type?: ButtonHTMLAttributes<unknown>['type'];
    } & (
      | {
          onConfirm: () => Promise<void> | void;
          withCancelButton?: true;
          onClose: () => void;
        }
      | {
          withCancelButton: false;
          onConfirm: () => Promise<void> | void;
          onClose?: () => void;
        }
    ))
  | {
      type: 'submit';
      onConfirm?: never;
      onClose: () => void;
      withCancelButton?: never;
    }
);

export const ModalButtons = ({
  ref,
  cancellationText = 'Cancel',
  confirmationTooltip,
  leftComponent = <Space />,
  withCancelButton = true,
  confirmationText = withCancelButton ? 'Save' : 'Got it!',
  confirmationCount,
  buttonColor = typeof confirmationText === 'string' && confirmationText.toLowerCase() === 'delete' ? 'red' : undefined,
  buttonVariant = withCancelButton ? undefined : 'light',
  loading,
  disabled,
  hideDivider,
  onClose,
  onConfirm,
  type,
}: ModalButtonProps) => (
  <Box
    display="flex"
    p={hideDivider ? undefined : 'md'}
    bg="white"
    style={{
      borderTop: hideDivider ? undefined : MODAL_DIVIDER_BORDER,
      position: 'sticky',
      bottom: 0,
      zIndex: 'var(--mantine-z-index-modal)',
      justifyContent: 'space-between',
      gap: 'xs',
      alignItems: 'center',
    }}
  >
    {leftComponent}
    <Flex gap="xs">
      {withCancelButton && (
        <Button onClick={onClose} variant="light" disabled={loading}>
          {cancellationText}
        </Button>
      )}
      <AgGridStyleTooltip label={confirmationTooltip} hidden={!confirmationTooltip} openDelay={0}>
        <Button
          ref={ref}
          color={buttonColor}
          loading={loading}
          disabled={disabled}
          onClick={onConfirm}
          type={type}
          variant={buttonVariant}
        >
          <Flex gap={6}>
            <span>{confirmationText}</span>
            {isNotNil(confirmationCount) && <span style={{ fontWeight: 400 }}>({confirmationCount})</span>}
          </Flex>
        </Button>
      </AgGridStyleTooltip>
    </Flex>
  </Box>
);
